import {
  forwardRef,
  memo,
  ReactNode,
  useState,
  useContext,
  useRef,
  useEffect,
  useImperativeHandle,
} from 'react';
import { Dropdown, Input, Menu, message, ModalProps, Select } from 'antd';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';
import { useMutation } from '@tanstack/react-query';
import createNamedContext from 'utils/createNamedContext';
import { ItemV5 } from '../types';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import insertAtCursor from 'utils/insertAtCursor';
import { css } from '@emotion/css';
import { useItemContext } from '@knowledge/common/components/ItemSelectV5/Context';
import { queryItemListByKeywordV5 } from '@knowledge/common/components/ItemSelectV5/api';
import { SupplementalKnowledgeAiReplyKnowledge } from 'services/api/supplementKnowledge';

type Type = 2 | 3 | 4;

export interface QuickSelectProps {
  // callback: (items: ItemV5[]) => void;
  bindItem?: SupplementalKnowledgeAiReplyKnowledge['bindItem'];
}
type RefProps = {
  onSelect: () => void;
};

export function filterByBindItem(
  items: ItemV5[],
  bindItem: SupplementalKnowledgeAiReplyKnowledge['bindItem'],
): ItemV5[] {
  return items
    .filter(({ externalItemId }) => bindItem.some(({ spuId }) => spuId === externalItemId))
    .map((item) => {
      const target = bindItem.find((_) => _.spuId === item.externalItemId);
      return {
        ...item,
        skuInfo: item.skuInfo.filter((_) => target?.skus.some(({ skuId }) => skuId === _.skuId)),
      };
    });
}

const QuickSelect = memo(
  forwardRef<RefProps, QuickSelectProps>(({ bindItem }, ref) => {
    const { setLoading, setDisabled } = useQuickSelectContext();
    const { addToLoadedItems, toggleBatch, toggleSkus } = useItemContext();
    // const { callback } = props;
    const { t } = useTranslate();
    const typeLabelMap = {
      4: t('sider.SKU货号'),
      2: t('sider.商品ID'),
      3: t('aide.主商品货号'),
    };
    const options = Object.entries(typeLabelMap).map(([value, label]) => {
      return { value: parseInt(value, 10), label };
    });
    const typePlaceholderMap = {
      2: t('knowledge.item.quickMode', { x: t('sider.商品ID') }),
      3: t('knowledge.item.quickMode', { x: t('aide.主商品货号') }),
      4: t('knowledge.item.quickMode', { x: t('sider.SKU货号') }),
    };
    const textareaRef = useRef<TextAreaRef>(null);
    const [selectType, setSelectType] = useState<Type>(4);
    const [value, setValue] = useState('');
    const onPaste = () => {
      navigator.clipboard
        .readText()
        .then((text) => {
          const { current: textarea } = textareaRef;

          if (textarea && text) {
            const { resizableTextArea } = textarea;

            if (resizableTextArea) {
              const { textArea } = resizableTextArea;

              const nextValue = insertAtCursor(textArea, text);

              setValue(nextValue);
            }
          }
        })
        .catch(() => {});
    };
    const { isLoading, mutateAsync } = useMutation({
      mutationFn: (...params: Parameters<typeof queryItemListByKeywordV5>) => {
        return queryItemListByKeywordV5(...params);
      },
    });
    useImperativeHandle(ref, () => {
      return {
        onSelect: async () => {
          var ids = value.split('\n').filter(Boolean);
          ids = ids
            .flat()
            .map((e) => e?.split(','))
            .filter(Boolean)
            .flat();
          ids = ids
            .flat()
            .map((e) => e?.split('，'))
            .filter(Boolean)
            .flat();
          ids = ids.map((e) => e?.trim());

          mutateAsync({
            keyword: ids.join(','),
            keywordType: selectType,
            platformStoreScope: undefined,
          }).then((res) => {
            const itemList = bindItem ? filterByBindItem(res.itemList, bindItem) : res.itemList;
            addToLoadedItems(itemList as ItemV5[]);
            // callback(itemList as ItemV5[]);
            const unfoundIds = ids.filter((id) => {
              return itemList.every((item: ItemSelectV2.Item) => {
                const { externalItemId, itemSku, skuInfo } = item;

                if (selectType === 2) {
                  return id !== externalItemId;
                }

                if (selectType === 3) {
                  return id !== itemSku;
                }

                return skuInfo.every(
                  ({ sellerSku }) => !sellerSku.toLowerCase().includes(id.toLowerCase()),
                );
              });
            });
            if (unfoundIds.length > 0) {
              message.warn(t('aide.x项未找到对应商品', { x: unfoundIds.length }));
              setTimeout(() => {
                setValue(unfoundIds.join('\n'));
              });
            } else {
              setTimeout(() => {
                setValue('');
              });
            }
            if (selectType === 4) {
              const skus = itemList.reduce<{ internalItemId: string; skuId: string }[]>(
                (res, { internalItemId, skuInfo }) => {
                  return skuInfo.reduce((res, { sellerSku, skuId }) => {
                    if (ids.some((id) => sellerSku.toLowerCase().includes(id.toLowerCase()))) {
                      res.push({
                        internalItemId,
                        skuId,
                      });
                    }
                    return res;
                  }, res);
                },
                [],
              );
              setTimeout(() => {
                toggleSkus(true, skus);
              });
              if (!unfoundIds.length) {
                message.success(t('aide.已选中x项', { x: skus.length }));
              }
            } else {
              setTimeout(() => {
                toggleBatch(
                  itemList.map(({ internalItemId }) => internalItemId),
                  true,
                );
              });
              if (!unfoundIds.length) {
                message.success(t('aide.已选中x项', { x: itemList.length }));
              }
            }
          });
        },
      };
    });
    useEffect(() => {
      setLoading(isLoading);
    }, [isLoading, setLoading]);
    useEffect(() => {
      setDisabled(!value);
    }, [value, setDisabled]);
    return (
      <div>
        <div className="pb-[16px]">
          <span>{t('aide.选择类型')}: </span>
          <Select
            dropdownMatchSelectWidth={false}
            value={selectType}
            onChange={setSelectType}
            options={options}
          />
        </div>
        <div className="">
          <div className="">
            <div className="">
              <Dropdown
                trigger={['contextMenu']}
                overlay={
                  <Menu>
                    <Menu.Item onClick={onPaste}>{t('ViewsChat.粘贴')}</Menu.Item>
                  </Menu>
                }
              >
                <Input.TextArea
                  autoSize={{ minRows: 12 }}
                  ref={textareaRef}
                  placeholder={
                    selectType === 4 ? t('select-sku-tips') : typePlaceholderMap[selectType]
                  }
                  value={value}
                  onChange={({ target: { value } }) => setValue(value)}
                />
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
    );
  }),
);

QuickSelect.displayName = 'ItemV5QuickSelect';

export interface QuickSelectModalProps extends ModalProps, QuickSelectProps {}

export const QuickSelectModal = memo((props: QuickSelectModalProps) => {
  const { bindItem, ...restProps } = props;
  const { t } = useTranslate();
  const ref = useRef<RefProps>(null);
  return (
    <QuickSelectProvider>
      {(loading, disabled) => {
        return (
          <JustRightHeightModal
            width={600}
            className={css`
              .ant-modal-body {
                padding: 16px;
              }
            `}
            title={t('knowledge.item.select.mode.mult')}
            okText={t('knowledge.item.select.mode.mult')}
            okButtonProps={{
              loading,
              disabled,
            }}
            onOk={() => {
              ref.current?.onSelect();
            }}
            {...restProps}
          >
            <QuickSelect bindItem={bindItem} ref={ref} />
          </JustRightHeightModal>
        );
      }}
    </QuickSelectProvider>
  );
});

QuickSelectModal.displayName = 'ItemV5QuickSelectModal';

const QuickSelectContext = createNamedContext<{
  loading: boolean;
  setLoading: (loading: boolean) => void;
  disabled: boolean;
  setDisabled: (disabled: boolean) => void;
}>('ItemV5QuickSelectContext', null!);

const QuickSelectProvider = memo(
  (props: { children: ReactNode | ((laoding: boolean, disabled: boolean) => ReactNode) }) => {
    const { children } = props;
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(true);
    return (
      <QuickSelectContext.Provider
        value={{
          loading,
          setLoading,
          disabled,
          setDisabled,
        }}
      >
        {typeof children === 'function' ? children(loading, disabled) : children}
      </QuickSelectContext.Provider>
    );
  },
);

const useQuickSelectContext = () => {
  const value = useContext(QuickSelectContext);
  if (!value) {
    throw new Error('useQuickSelectContext must be used within a QuickSelectProvider');
  }
  return value;
};
